import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../context/AuthContext';
import UniformCampaignBanner from '../../components/UniformCampaignBanner';
import DocumentNotificationBanner from '../../components/DocumentNotificationBanner';
import EventNotificationBanner from '../../components/EventNotificationBanner';
import { usePullToRefresh, RefreshIndicator } from '../../hooks/usePullToRefresh';
import GameRsvpAlertBanner from '../../components/GameRsvpAlertBanner';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Users,
  CreditCard,
  ClipboardList,
  Calendar,
  DollarSign,
  Star,
  Clock,
  Pause,
  MessageCircle,
  Activity,
} from 'lucide-react';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const FOCUS_AREAS = ['Batting', 'Bowling', 'Fielding', 'Wicket Keeping'];

const ParentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [_players, setPlayers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [trialRequests, setTrialRequests] = useState([]);
  const [childrenSchedule, setChildrenSchedule] = useState([]);
  const [teamChats, setTeamChats] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Trial Request Dialog
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);
  const [trialFormData, setTrialFormData] = useState({
    child_name: '',
    date_of_birth: '',
    skill_level: 'beginner',
    cricket_experience: '',
    preferred_days: [],
    focus_areas: [],
    additional_notes: ''
  });

  const { refreshing, onTouchStart, onTouchEnd } = usePullToRefresh(async () => {
    await fetchData();
  });

  const fetchData = async () => {
    try {
      const [statsRes, playersRes, enrollmentsRes, trialsRes, chatsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/players'),
        api.get('/enrollments'),
        api.get('/trial-requests'),
        api.get('/chat/teams').catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setPlayers(playersRes.data);
      setEnrollments(enrollmentsRes.data);
      setTrialRequests(trialsRes.data);
      setTeamChats(chatsRes.data || []);
      
      // Build schedule from enrollments that have class info
      const scheduleData = [];
      for (const enrollment of enrollmentsRes.data) {
        if (enrollment.class_name && ['current', 'approved'].includes(enrollment.status)) {
          scheduleData.push({
            id: enrollment.id,
            player_name: enrollment.player_name,
            program_name: enrollment.program_name,
            class_name: enrollment.class_name,
            class_day: enrollment.class_day,
            class_time: enrollment.class_time,
            status: enrollment.status
          });
        }
      }
      
      // Sort schedule by day of week, then by time
      const dayOrder = {
        'monday': 0, 'tuesday': 1, 'wednesday': 2, 'thursday': 3,
        'friday': 4, 'saturday': 5, 'sunday': 6
      };
      scheduleData.sort((a, b) => {
        const dayA = dayOrder[(a.class_day || '').toLowerCase()] ?? 99;
        const dayB = dayOrder[(b.class_day || '').toLowerCase()] ?? 99;
        if (dayA !== dayB) return dayA - dayB;
        return (a.class_time || '').localeCompare(b.class_time || '');
      });
      
      setChildrenSchedule(scheduleData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleTrialRequest = async (e) => {
    e.preventDefault();
    if (!trialFormData.child_name || !trialFormData.date_of_birth) {
      toast.error('Please fill in required fields');
      return;
    }
    if (trialFormData.preferred_days.length === 0) {
      toast.error('Please select at least one preferred day');
      return;
    }
    try {
      await api.post('/trial-requests', trialFormData);
      toast.success('Trial class request submitted! We will contact you shortly.');
      setTrialDialogOpen(false);
      setTrialFormData({
        child_name: '',
        date_of_birth: '',
        skill_level: 'beginner',
        cricket_experience: '',
        preferred_days: [],
        focus_areas: [],
        additional_notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit trial request');
    }
  };

  const togglePreferredDay = (day) => {
    setTrialFormData(prev => {
      const isSelected = prev.preferred_days.includes(day);
      if (!isSelected && prev.preferred_days.length >= 3) {
        toast.error('You can select up to 3 preferred days');
        return prev;
      }
      return {
        ...prev,
        preferred_days: isSelected
          ? prev.preferred_days.filter(d => d !== day)
          : [...prev.preferred_days, day]
      };
    });
  };

  const toggleFocusArea = (area) => {
    setTrialFormData(prev => ({
      ...prev,
      focus_areas: prev.focus_areas.includes(area)
        ? prev.focus_areas.filter(a => a !== area)
        : [...prev.focus_areas, area]
    }));
  };

  const handlePayment = async (enrollmentId) => {
    try {
      const enrollment = enrollments.find(e => e.id === enrollmentId);
      if (!enrollment) {
        toast.error('Enrollment not found');
        return;
      }
      
      const response = await api.post('/billing/enrollment/create', {
        enrollment_id: enrollmentId,
        payment_frequency: enrollment.payment_frequency || 'monthly',
        origin_url: window.location.origin
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  // Show Pay Now for any non-settled status: pending (active checkout / future),
  // unpaid (no Stripe session, money still owed), or overdue. Excludes paid /
  // refunded / void / cancelled.
  const pendingPayments = enrollments.filter(
    e => e.status === 'approved' && ['pending', 'unpaid', 'overdue'].includes(String(e.payment_status || '').toLowerCase())
  );

  const pauseRequestedEnrollments = enrollments.filter(
    e => e.status === 'pause_requested'
  );

  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Parent Dashboard">
      <div className="space-y-6" data-testid="parent-dashboard" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <RefreshIndicator refreshing={refreshing} />
        {/* Uniform Campaign Banner - Prominent at top */}
        <UniformCampaignBanner />
        
        {/* Document Notification Banner */}
        <DocumentNotificationBanner />
        
        {/* Event Notification Banner */}
        <EventNotificationBanner />
        
        {/* Game RSVP Alert Banner */}
        <GameRsvpAlertBanner />

        {/* Welcome */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-4 lg:p-6 text-white">
          <h2 className="font-heading text-xl lg:text-2xl font-bold">Welcome, {user?.name}!</h2>
          <p className="text-slate-300 mt-1 text-sm lg:text-base">Manage your children's cricket training</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
          <Card 
            className="border border-slate-200 cursor-pointer hover:border-blue-300 hover:shadow-md transition-all"
            onClick={() => navigate('/parent/children')}
            data-testid="stat-card-children"
          >
            <CardContent className="p-3 lg:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-slate-500">My Family</p>
                  <p className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 mt-1">
                    {stats?.total_children || 0}
                  </p>
                </div>
                <div className="p-2 lg:p-3 rounded-lg bg-blue-500">
                  <Users className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border border-slate-200 cursor-pointer hover:border-green-300 hover:shadow-md transition-all"
            onClick={() => navigate('/parent/enrollments')}
            data-testid="stat-card-enrollments"
          >
            <CardContent className="p-3 lg:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-slate-500">Enrollments</p>
                  <p className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 mt-1">
                    {stats?.total_enrollments || 0}
                  </p>
                </div>
                <div className="p-2 lg:p-3 rounded-lg bg-green-500">
                  <ClipboardList className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border border-slate-200 cursor-pointer hover:border-purple-300 hover:shadow-md transition-all"
            onClick={() => navigate('/parent/enrollments')}
            data-testid="stat-card-enrollments"
          >
            <CardContent className="p-3 lg:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-slate-500">Active Enrollments</p>
                  <p className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 mt-1">
                    {stats?.active_subscriptions || stats?.total_enrollments || 0}
                  </p>
                </div>
                <div className="p-2 lg:p-3 rounded-lg bg-purple-500">
                  <Calendar className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border border-slate-200 cursor-pointer hover:border-amber-300 hover:shadow-md transition-all"
            onClick={() => navigate('/parent/billing')}
            data-testid="stat-card-payments"
          >
            <CardContent className="p-3 lg:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-slate-500">Pending Payments</p>
                  <p className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 mt-1">
                    {stats?.pending_payments || 0}
                  </p>
                </div>
                <div className="p-2 lg:p-3 rounded-lg bg-amber-500">
                  <CreditCard className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border border-slate-200 cursor-pointer hover:border-rose-300 hover:shadow-md transition-all"
            onClick={() => navigate('/parent/games')}
            data-testid="stat-card-games-played"
          >
            <CardContent className="p-3 lg:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-slate-500">Games Played</p>
                  <p className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 mt-1">
                    {stats?.total_games_played || 0}
                  </p>
                </div>
                <div className="p-2 lg:p-3 rounded-lg bg-rose-500">
                  <Activity className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Payments Alert */}
        {pendingPayments.length > 0 && (
          <Card className="border-2 border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg text-amber-800 flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Pending Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingPayments.map((enrollment) => (
                  <div 
                    key={enrollment.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between bg-white p-3 lg:p-4 rounded-lg gap-2"
                    data-testid={`pending-payment-${enrollment.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">{enrollment.player_name}</p>
                      <p className="text-sm text-slate-500">{enrollment.program_name}</p>
                    </div>
                    <div className="flex items-center gap-3 sm:gap-4">
                      <p className="font-semibold text-slate-900">
                        {cs()}{enrollment.fee_amount?.toFixed(2)}
                      </p>
                      <Button
                        onClick={() => handlePayment(enrollment.id)}
                        className="bg-amber-500 hover:bg-amber-600 flex-1 sm:flex-none"
                        size="sm"
                        data-testid={`pay-now-${enrollment.id}`}
                      >
                        Pay Now
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Pause Requests */}
        {pauseRequestedEnrollments.length > 0 && (
          <Card className="border-2 border-orange-200 bg-orange-50">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg text-orange-800 flex items-center gap-2">
                <Pause className="h-5 w-5" />
                Pending Pause Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pauseRequestedEnrollments.map((enrollment) => (
                  <div 
                    key={enrollment.id}
                    className="flex items-center justify-between bg-white p-4 rounded-lg"
                    data-testid={`pause-request-${enrollment.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">{enrollment.player_name}</p>
                      <p className="text-sm text-slate-500">{enrollment.program_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Awaiting Approval
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Children's Schedule */}
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-500" />
              Children's Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {childrenSchedule.length === 0 ? (
              <div className="p-8 text-center">
                <Calendar className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No active classes scheduled</p>
                <p className="text-sm text-slate-400 mt-1">
                  Enroll your children in a program to see their schedule
                </p>
                <Button 
                  className="mt-4 bg-blue-500 hover:bg-blue-600"
                  onClick={() => navigate('/parent/programs')}
                >
                  Browse Programs
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {childrenSchedule.map((schedule) => (
                  <div 
                    key={schedule.id}
                    className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    data-testid={`schedule-${schedule.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-bold">
                          {schedule.player_name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{schedule.player_name}</p>
                        <p className="text-sm text-slate-500">{schedule.program_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-slate-700">{schedule.class_name}</p>
                      {schedule.class_day && (
                        <p className="text-sm text-slate-500 capitalize">
                          {schedule.class_day} {schedule.class_time && `• ${schedule.class_time}`}
                        </p>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        schedule.status === 'current' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {schedule.status === 'current' ? 'Active' : 'Approved'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team Chats Card */}
        {teamChats.length > 0 && (
          <Card className="border border-slate-200">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-blue-500" />
                Team Chats
                {teamChats.reduce((sum, c) => sum + (c.unread_count || 0), 0) > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                    {teamChats.reduce((sum, c) => sum + (c.unread_count || 0), 0)} new
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {teamChats.slice(0, 4).map((chat) => (
                  <div 
                    key={chat.team_id}
                    className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate('/parent/tournaments')}
                    data-testid={`chat-preview-${chat.team_id}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <MessageCircle className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900 truncate">{chat.team_name}</p>
                          {chat.unread_count > 0 && (
                            <span className="bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 flex-shrink-0">
                              {chat.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 truncate">{chat.tournament_name}</p>
                        {chat.last_message && (
                          <p className="text-sm text-slate-600 truncate mt-1">
                            <span className="font-medium">{chat.last_message.sender_name}:</span>{' '}
                            {chat.last_message.content}
                          </p>
                        )}
                      </div>
                    </div>
                    {chat.last_message && (
                      <span className="text-xs text-slate-400 ml-2 flex-shrink-0">
                        {new Date(chat.last_message.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              {teamChats.length > 4 && (
                <div className="p-3 border-t border-slate-100 text-center">
                  <Button 
                    variant="link" 
                    className="text-blue-600"
                    onClick={() => navigate('/parent/tournaments')}
                  >
                    View all {teamChats.length} team chats
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Request Trial Class Card */}
        <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-white">
          <CardContent className="p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 lg:gap-4">
              <div className="flex items-center gap-3 lg:gap-4">
                <div className="p-2 lg:p-3 bg-blue-500 rounded-lg">
                  <Star className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-base lg:text-xl font-bold text-slate-900">Try a Free Trial Class!</h3>
                  <p className="text-slate-600 text-xs lg:text-sm">Experience our cricket training before enrolling</p>
                </div>
              </div>
              <Dialog open={trialDialogOpen} onOpenChange={setTrialDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-500 hover:bg-blue-600" data-testid="request-trial-btn">
                    <Star className="h-4 w-4 mr-2" />
                    Request Trial Class
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">Request a Trial Class</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleTrialRequest} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Child's Full Name *</Label>
                      <Input
                        value={trialFormData.child_name}
                        onChange={(e) => setTrialFormData({ ...trialFormData, child_name: e.target.value })}
                        placeholder="Enter child's name"
                        required
                        data-testid="trial-child-name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Date of Birth *</Label>
                      <Input
                        type="date"
                        value={trialFormData.date_of_birth}
                        onChange={(e) => setTrialFormData({ ...trialFormData, date_of_birth: e.target.value })}
                        required
                        data-testid="trial-dob"
                      />
                      {trialFormData.date_of_birth && (
                        <p className="text-sm text-blue-600">
                          Age: {Math.floor((new Date() - new Date(trialFormData.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000))} years old
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Current Skill Level *</Label>
                      <Select
                        value={trialFormData.skill_level}
                        onValueChange={(value) => setTrialFormData({ ...trialFormData, skill_level: value })}
                      >
                        <SelectTrigger data-testid="trial-skill-level">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner - New to cricket</SelectItem>
                          <SelectItem value="intermediate">Intermediate - Some experience</SelectItem>
                          <SelectItem value="advanced">Advanced - Competitive player</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Cricket Experience</Label>
                      <Textarea
                        value={trialFormData.cricket_experience}
                        onChange={(e) => setTrialFormData({ ...trialFormData, cricket_experience: e.target.value })}
                        placeholder="Describe any previous cricket experience (teams, years played, etc.)"
                        rows={2}
                        data-testid="trial-experience"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Preferred Days for Trial * <span className="text-xs text-slate-500">(max 3)</span></Label>
                      <div className="grid grid-cols-2 gap-2">
                        {DAYS_OF_WEEK.map((day) => (
                          <label
                            key={day}
                            className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                              trialFormData.preferred_days.includes(day)
                                ? 'bg-blue-50 border-blue-300'
                                : 'bg-white border-slate-200 hover:bg-slate-50'
                            }`}
                          >
                            <Checkbox
                              checked={trialFormData.preferred_days.includes(day)}
                              onCheckedChange={() => togglePreferredDay(day)}
                            />
                            <span className="text-sm">{day}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Areas to Focus On</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {FOCUS_AREAS.map((area) => (
                          <label
                            key={area}
                            className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                              trialFormData.focus_areas.includes(area)
                                ? 'bg-blue-50 border-blue-300'
                                : 'bg-white border-slate-200 hover:bg-slate-50'
                            }`}
                          >
                            <Checkbox
                              checked={trialFormData.focus_areas.includes(area)}
                              onCheckedChange={() => toggleFocusArea(area)}
                            />
                            <span className="text-sm">{area}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Additional Notes</Label>
                      <Textarea
                        value={trialFormData.additional_notes}
                        onChange={(e) => setTrialFormData({ ...trialFormData, additional_notes: e.target.value })}
                        placeholder="Any other information you'd like us to know"
                        rows={2}
                        data-testid="trial-notes"
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setTrialDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-blue-500 hover:bg-blue-600" data-testid="submit-trial-btn">
                        Submit Request
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Trial Requests Status */}
        {trialRequests.length > 0 && (
          <Card className="border border-slate-200">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="font-heading text-lg">My Trial Requests</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {trialRequests.map((trial) => (
                  <div key={trial.id} className="p-4 flex items-center justify-between" data-testid={`trial-${trial.id}`}>
                    <div>
                      <p className="font-medium text-slate-900">{trial.child_name}</p>
                      <p className="text-sm text-slate-500">
                        Age: {trial.age} • {trial.skill_level}
                        {trial.trial_date && ` • Trial: ${trial.trial_date}`}
                      </p>
                      {trial.assigned_class_name && (
                        <p className="text-sm text-blue-600">Class: {trial.assigned_class_name}</p>
                      )}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                      trial.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      trial.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                      trial.status === 'completed' ? 'bg-green-100 text-green-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {trial.status}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ParentDashboard;
